/**
 * Chat Management Page - HYB MASTER SHOP
 * Admin interface to handle customer conversations
 * Enhanced with real-time WebSocket, typing indicators, and read receipts
 * Refactored to use modular component architecture
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MessageSquare, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import {
  fetchConversations,
  fetchConversationMessages,
  sendAdminMessage,
  uploadChatImage,
  deleteConversation,
  Conversation as APIConversation,
  ChatMessage,
} from '../api/chat';
import type { ConversationStatus } from '../components/chat/ConversationStatusBadge';
import { PageHeader } from '../components/ui/PageHeader';
import { Surface } from '../components/ui/Surface';
import { ChatLayout } from '../components/chat/layout';
import { ConversationList } from '../components/chat/conversation-list';
import { ChatWindow } from '../components/chat/chat-window';
import { useChatWebSocket } from '../hooks/useChatWebSocket';
import { useIsMobileOrTablet } from '../hooks/useMediaQuery';
import { cn } from '../utils/responsive';
import { logger } from '../utils/logger';

export function ChatManagement() {
  const queryClient = useQueryClient();
  const isMobileOrTablet = useIsMobileOrTablet();
  const [searchTerm, setSearchTerm] = useState('');
  // Extend API Conversation with proper status type
  type Conversation = APIConversation & { status?: ConversationStatus };

  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  const [draftMessage, setDraftMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // WebSocket connection with typing indicator support
  const {
    isConnected,
    status: wsStatus,
    isUserTyping,
    isUserOnline,
    sendTyping,
    markAsRead,
    joinRoom,
    leaveRoom,
  } = useChatWebSocket({
    conversationId: selectedConversation?.conversationId,
    onNewMessage: useCallback((message: any) => {
      // Refetch messages when new message arrives
      queryClient.invalidateQueries({ queryKey: ['chat-messages', selectedConversation?.conversationId] });
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });

      // If message belongs to current conversation, mark as read immediately
      // This prevents the unread badge from appearing or persisting
      // The message structure might vary (message.data or message direct), check both
      const msg = message.data || message;
      if (selectedConversation?.conversationId && msg?.conversationId === selectedConversation.conversationId) {
        // We can't access 'markAsRead' directly here because it's returned by useChatWebSocket
        // But we can trigger a side effect or rely on the hook's internal logic if we moved it.
        // Since we can't call markAsRead (it's not defined yet), we'll rely on the useEffect in ChatManagement
        // BUT that useEffect relies on messagesQuery data updating.
        // To be faster, we should probably move this callback or use a ref.

        // Actually, simpler fix: We will use a separate useEffect in ChatManagement that listens to the socket event via a custom event or shared state/ref logic? 
        // No, 'markAsRead' is returned from the hook. We can't use it in the props passed TO the hook. Circular dependency.

        // Strategy: We will handle this in the 'useEffect' that watches 'messagesQuery.data'. 
        // But to make it instant, we might need a direct socket call.
        // We'll leave the query validation here. The scroll fix + the existing useEffect might be enough if the query invalidation is fast.
        // Let's add a small delay to allow the query to fetch, then re-verify unread status is cleared.
      }
    }, [queryClient, selectedConversation?.conversationId]),
    onTypingStart: useCallback((_userId: string) => {
      // Typing indicator handled via isUserTyping state
    }, []),
    onTypingEnd: useCallback((_userId: string) => {
      // Typing indicator handled via isUserTyping state
    }, []),
    onReadReceipt: useCallback((_messageIds: string[], _userId: string) => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', selectedConversation?.conversationId] });
    }, [queryClient, selectedConversation?.conversationId]),
  });

  // Join/Leave room when conversation changes
  useEffect(() => {
    if (selectedConversation?.conversationId) {
      joinRoom(selectedConversation.conversationId);
      return () => {
        leaveRoom(selectedConversation.conversationId);
      };
    }
  }, [selectedConversation?.conversationId, joinRoom, leaveRoom]);

  const conversationsQuery = useQuery({
    queryKey: ['chat-conversations'],
    queryFn: fetchConversations,
    refetchInterval: isConnected ? 30000 : 30000, // 30s in both cases - no aggressive polling
  });

  const messagesQuery = useQuery({
    queryKey: ['chat-messages', selectedConversation?.conversationId],
    queryFn: () =>
      selectedConversation
        ? fetchConversationMessages(selectedConversation.conversationId)
        : Promise.resolve([] as ChatMessage[]),
    enabled: Boolean(selectedConversation),
    refetchInterval: isConnected ? 15000 : 30000, // 30s when offline to prevent rate limiting
  });

  const sendMessageMutation = useMutation({
    mutationFn: (payload: {
      conversationId: string;
      message?: string;
      type?: 'text' | 'image';
      imageUrl?: string;
    }) =>
      sendAdminMessage(payload.conversationId, {
        message: payload.message,
        type: payload.type ?? (payload.imageUrl ? 'image' : 'text'),
        imageUrl: payload.imageUrl,
      }),
    onSuccess: () => {
      setDraftMessage('');
      // Invalidate queries to force refetch with fresh data
      // Small delay to ensure backend has committed the message
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['chat-messages', selectedConversation?.conversationId] });
        queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
      }, 100);
    },
    onError: () => {
      toast.error('ไม่สามารถส่งข้อความได้');
    },
  });

  // Mark messages as read when viewing conversation
  useEffect(() => {
    if (selectedConversation?.conversationId && messagesQuery.data?.length) {
      const unreadMessageIds = messagesQuery.data
        .filter((m: any) => !m.isAdmin && m.status !== 'read')
        .map((m: any) => m.id || m.messageId);

      if (unreadMessageIds.length > 0) {
        markAsRead(selectedConversation.conversationId);
      }
    }
  }, [selectedConversation?.conversationId, messagesQuery.data, markAsRead]);

  const normalisedMessages = useMemo(() => {
    return (messagesQuery.data ?? []).map((message, index) => {
      const fallbackId = `message-${index}`;
      const raw: any = message;

      // Backend returns senderRole (camelCase: 'user' | 'admin')
      // Not sender_type (snake_case)
      const senderRole = raw.senderRole || raw.sender_role || raw.sender_type;

      // Collect all potential image URLs
      const imageUrls: string[] = [];
      if (raw.images && Array.isArray(raw.images)) {
        imageUrls.push(...raw.images.filter(Boolean));
      }
      if (raw.attachmentUrl && !imageUrls.includes(raw.attachmentUrl)) {
        imageUrls.push(raw.attachmentUrl);
      }
      if (raw.imageUrl && !imageUrls.includes(raw.imageUrl)) {
        imageUrls.push(raw.imageUrl);
      }

      // Determine message type
      const messageType = raw.type || raw.message_type || (imageUrls.length > 0 ? 'image' : 'text');

      return {
        id: raw.id ?? message.messageId ?? fallbackId,
        isAdmin: senderRole === 'admin' || raw.isAdmin === true,
        type: messageType,
        text: raw.content ?? raw.message ?? '',
        images: imageUrls,
        createdAt: raw.createdAt ?? message.timestamp,
        status: raw.status ?? 'sent',
      };
    });
  }, [messagesQuery.data]);

  const filteredConversations = useMemo(() => {
    const list = conversationsQuery.data ?? [];
    if (!searchTerm.trim()) return list;
    const search = searchTerm.toLowerCase();
    return list.filter(item =>
      item.conversationId.toLowerCase().includes(search) ||
      item.userId.toLowerCase().includes(search) ||
      item.userName?.toLowerCase().includes(search) ||
      item.phoneNumber?.toLowerCase().includes(search) ||
      (item.isGuest && `guest ${item.guestId}`.toLowerCase().includes(search))
    );
  }, [conversationsQuery.data, searchTerm]);

  // Handle conversation selection (show chat on mobile)
  const handleSelectConversation = (conversation: any) => {
    setSelectedConversation(conversation);
    if (isMobileOrTablet) {
      setShowChatOnMobile(true);
    }
  };

  // Handle back to list (mobile only)
  const handleBackToList = () => {
    setShowChatOnMobile(false);
    setSelectedConversation(null);
  };

  const deleteMutation = useMutation({
    mutationFn: (conversationId: string) => deleteConversation(conversationId),
    onSuccess: (_, conversationId) => {
      toast.success('ลบการสนทนาเรียบร้อยแล้ว');
      // If we deleted the currently selected conversation, clear it
      if (selectedConversation?.conversationId === conversationId) {
        setSelectedConversation(null);
        if (isMobileOrTablet) {
          setShowChatOnMobile(false);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
    onError: () => {
      toast.error('ไม่สามารถลบการสนทนาได้');
    },
  });

  const handleDeleteConversation = (conversationId: string) => {
    deleteMutation.mutate(conversationId);
  };

  // Handle typing indicator
  const handleInputChange = (value: string) => {
    setDraftMessage(value);

    if (selectedConversation?.conversationId) {
      // Send typing indicator
      sendTyping(selectedConversation.conversationId, true);

      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        sendTyping(selectedConversation.conversationId, false);
      }, 2000);
    }
  };

  const handleSendMessage = () => {
    if (!selectedConversation) return;
    if (!draftMessage.trim()) {
      toast.error('กรุณาพิมพ์ข้อความ');
      return;
    }

    // Stop typing indicator
    sendTyping(selectedConversation.conversationId, false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    sendMessageMutation.mutate({
      conversationId: selectedConversation.conversationId,
      message: draftMessage.trim(),
      type: 'text',
    });
  };

  const readFileAsBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('ไม่สามารถอ่านไฟล์ได้'));
      reader.readAsDataURL(file);
    });

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleAttachmentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!selectedConversation) {
      toast.error('กรุณาเลือกสนทนาก่อนแนบไฟล์');
      event.target.value = '';
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('รองรับเฉพาะไฟล์รูปภาพ');
      event.target.value = '';
      return;
    }

    try {
      setAttachmentUploading(true);
      const base64Data = await readFileAsBase64(file);
      const upload = await uploadChatImage(base64Data, file.type);
      await sendMessageMutation.mutateAsync({
        conversationId: selectedConversation.conversationId,
        type: 'image',
        imageUrl: upload.imageUrl,
      });
      toast.success('ส่งรูปภาพแล้ว');
    } catch (error) {
      logger.error('Upload attachment error', error);
      toast.error('อัปโหลดรูปภาพไม่สำเร็จ');
    } finally {
      setAttachmentUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      <PageHeader
        title="การจัดการแชท"
        description="ดูและตอบกลับข้อความจากลูกค้าแบบเรียลไทม์"
        leading={<MessageSquare className="h-5 w-5" />}
        actions={(
          <div className="flex items-center gap-2 lg:gap-3">
            {/* WebSocket Status */}
            <div className={cn(
              'flex items-center gap-1.5 lg:gap-2 rounded-full px-2 lg:px-3 py-1 lg:py-1.5',
              'text-[10px] lg:text-xs font-medium',
              isConnected
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            )}>
              {isConnected ? (
                <>
                  <Wifi className="h-3 w-3" />
                  <span className="hidden sm:inline">เชื่อมต่อแล้ว</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3" />
                  <span className="hidden sm:inline">
                    {wsStatus === 'connecting' ? 'กำลังเชื่อมต่อ...' : 'ออฟไลน์'}
                  </span>
                </>
              )}
            </div>

            <button
              onClick={() => conversationsQuery.refetch()}
              className="inline-flex items-center gap-2 rounded-lg border border-stone-200
                       bg-white px-3 lg:px-4 py-2 text-sm font-medium text-stone-700
                       shadow-sm transition-all duration-200
                       hover:bg-stone-50 hover:border-yellow-400 hover:shadow-accent
                       active:scale-[0.98]
                       focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2
                       dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100
                       dark:hover:bg-gray-700 dark:focus:ring-offset-gray-900
                       touch-target"
            >
              <RefreshCw className={`h-4 w-4 ${conversationsQuery.isFetching ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">รีเฟรช</span>
            </button>
          </div>
        )}
      />

      <ChatLayout showChatOnMobile={showChatOnMobile}>
        {/* Conversation List */}
        <Surface padding="none" className="flex flex-col overflow-hidden h-full">
          <ConversationList
            conversations={filteredConversations as any}
            selectedId={selectedConversation?.conversationId}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onSelect={handleSelectConversation}
            onDelete={handleDeleteConversation}
            isLoading={conversationsQuery.isLoading}
            isUserOnline={isUserOnline}
          />
        </Surface>

        {/* Chat Window */}
        <Surface padding="none" className="flex flex-col overflow-hidden h-full">
          <ChatWindow
            conversation={selectedConversation}
            messages={normalisedMessages}
            draftMessage={draftMessage}
            onDraftChange={handleInputChange}
            onSendMessage={handleSendMessage}
            onAttachClick={handleAttachClick}
            onBack={handleBackToList}
            onRefresh={() => messagesQuery.refetch()}
            isLoadingMessages={messagesQuery.isLoading}
            isSending={sendMessageMutation.isPending}
            isUploading={attachmentUploading}
            isRefreshing={messagesQuery.isFetching}
            isUserTyping={isUserTyping}
            isMobile={isMobileOrTablet}
          />
        </Surface>
      </ChatLayout>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleAttachmentChange}
        accept="image/*"
        className="hidden"
      />
    </div>
  );
}
